"use client";

import { useSuspenseQuery } from "@apollo/client";
import { useI18n } from "@wojo/localization";
import { Spacing, ZendeskArticle } from "@wojo/storyblok";
import { Button, Markdown, Text } from "@wojo/ui";
import { ZendeskHelpCenterLink } from "@wojo/web-components";
import dayjs from "dayjs";
import {
    EntryRequirementsToursDocument,
    EntryRequirementsToursQuery,
    EntryRequirementsToursQueryVariables,
} from "generated/graphql";
import { useQueryState } from "nuqs";
import React, { useEffect, useState } from "react";
import { FiChevronLeft } from "react-icons/fi";
import { EntryRequirementsSegments } from "./EntryRequirementsSegments";
import styles from "./EntryRequirementsTour.module.scss";

export type EntryRequirementsTourProps = {
    destinationsSubtext: string | undefined;
    destinationsTitle: string | undefined;
    faqTitle: string | undefined;
    nationalityCode: string;
};

const legacyTourCodeLength = 3;
const legacySuffix = "G";

export const EntryRequirementsTour: React.FC<EntryRequirementsTourProps> = ({
    destinationsSubtext,
    destinationsTitle,
    faqTitle,
    nationalityCode: nationalityCountryCode,
}) => {
    // We will account for both tour code param formats for now, for historical content from all brands
    const [wojoTourCode, setTourCode] = useQueryState("tourCode", {
        history: "push",
    });
    //HACK: incrementing year is a workaround to get tour results for Adventures which doesn't have any 2024 tours.
    const [hasIncrementedYear, setHasIncrementedYear] = useState(false);
    const [legacyTourCode, setLegacyTourCode] = useQueryState("tour-code");
    const copy = useI18n("global");
    const tourCode = (wojoTourCode || legacyTourCode || "").toUpperCase();
    const { data } = useSuspenseQuery<
        EntryRequirementsToursQuery,
        EntryRequirementsToursQueryVariables
    >(EntryRequirementsToursDocument, {
        variables: {
            filter: {
                tourCode:
                    tourCode.length === legacyTourCodeLength
                        ? `${tourCode}${legacySuffix}`
                        : tourCode,
                priceYear: hasIncrementedYear
                    ? dayjs().add(1, "year").year()
                    : dayjs().year(),
            },
            language: process.env.NEXT_PUBLIC_STORYBLOK_LANGUAGE || "default",
            pagination: { limit: 1 },
        },
        skip: !tourCode,
        context: {
            fetchOptions: { next: { revalidate: 900 } },
            includeCookie: false,
        },
    });

    useEffect(() => {
        if (!!tourCode) {
            setHasIncrementedYear(false);
        }
    }, [tourCode]);

    useEffect(() => {
        if (!data?.tours.results.length && !hasIncrementedYear) {
            setHasIncrementedYear(true);
        }
    }, [data, hasIncrementedYear]);

    if (!tourCode) {
        return null;
    }

    const tour = data?.tours.results.at(0);
    if (data && !tour) {
        return (
            <NoResults
                onNavigate={() => {
                    setTourCode(null);
                    setLegacyTourCode(null);
                }}
            />
        );
    }
    const tourOvernightCountries: Array<string> =
        tour?.overnightCountries.map((country) => country.alpha3Code) || [];
    const allExtentionCountries: Array<string> =
        tour?.extensions.flatMap((ext) =>
            ext.overnightCountries.map((country) => country.alpha3Code),
        ) || [];
    const extentionCountries = allExtentionCountries.filter(
        (a) => !tourOvernightCountries.includes(a),
    );
    return (
        <div>
            <Button
                adornLeft={<FiChevronLeft />}
                onClick={() => {
                    setTourCode(null);
                    setLegacyTourCode(null);
                }}
                variant="quiet"
            >
                Search for a different tour
            </Button>
            {!!tour?.content?.name && (
                <Text.Heading className={styles.heading} order="2">
                    {tour.content.name}
                </Text.Heading>
            )}
            {destinationsTitle && (
                <Text.Heading className={styles.title} order="3">
                    {destinationsTitle}
                </Text.Heading>
            )}
            <a
                className={styles.sherpaLink}
                href="https://www.joinsherpa.com/"
                target="_blank"
                rel="noopener"
            >
                <span>
                    <img
                        alt="Powered by Sherpa"
                        src="https://img2.storyblok.com/f/53624/170x23/5ae895b863/powered-by-sherpa.png"
                    />
                </span>
            </a>
            {!!destinationsSubtext && (
                <Text.Body tag="div" className={styles.description}>
                    <Markdown markdown={destinationsSubtext} />
                </Text.Body>
            )}

            <EntryRequirementsSegments
                nationalityCountryCode={nationalityCountryCode}
                countryCodes={tourOvernightCountries}
            />
            {extentionCountries.length > 0 && (
                <>
                    <Text.Heading
                        className={styles["extension-title"]}
                        tag="h4"
                        order="5"
                    >
                        With {tour?.extensions.at(0)?.content?.name} extension
                    </Text.Heading>
                    <EntryRequirementsSegments
                        nationalityCountryCode={nationalityCountryCode}
                        countryCodes={extentionCountries}
                        originCountryCode={tourOvernightCountries.at(-1)}
                    />
                </>
            )}
            <Text.Heading className={styles.title} order="3">
                {faqTitle}
            </Text.Heading>
            <ZendeskArticle
                disableContainerPadding
                hideCategoryName
                layout="2"
                numberOfArticles="10"
                spaceAbove={Spacing.Medium}
                zendeskCategoryQuery="travel requirements"
            />
            <ZendeskHelpCenterLink className={styles.helpCenterButton}>
                {copy.global.helpCenter.label.l()}
            </ZendeskHelpCenterLink>
        </div>
    );
};

const NoResults: React.FC<{ onNavigate: () => void }> = ({ onNavigate }) => {
    return (
        <div>
            <Text.Heading order="3">Tour not found</Text.Heading>
            <Text.Body className={styles.notFound}>
                Sorry, we couldn&apos;t find your tour. Try{" "}
                <Button onClick={onNavigate} variant="quiet">
                    searching for another tour.
                </Button>
            </Text.Body>
        </div>
    );
};
